'use client';

import { FC, useEffect, useMemo, useRef } from 'react';
import { ClothesFinderWizardStoryblok } from 'storyblok-types';
import { useRouter } from 'modules/next/hooks';
import { WizardSteps } from '../../Finders/WizardSteps';
import { WizardHeader } from '../WizardHeader';
import { WizardStep } from '../WizardStep';
import { WizardItem } from '../data/types';
import {
  buildQueryParams,
  getSliderSizes,
  renderCategoryItems,
} from '../helpers';
import { useElementHeight } from '../hooks';
import { useClothesFinderWizardStore } from '../store';
import styles from './styles.module.css';

export const ClothesFinderWizard: FC<{
  blok: ClothesFinderWizardStoryblok;
}> = ({ blok }) => {
  const {
    currentStep,
    wizardData,
    setNextStep,
    setCurrentStep,
    updateStepData,
  } = useClothesFinderWizardStore();

  const router = useRouter();

  const firstStepRef = useRef<HTMLDivElement>(null);
  const secondStepRef = useRef<HTMLDivElement>(null);

  const steps = [1, 2, 3];
  const stepRefs = [firstStepRef, secondStepRef, null];

  const [firstStepHeight, secondStepHeight] = useElementHeight(
    [firstStepRef, secondStepRef],
    wizardData,
  );

  const { sliderHeight, sliderTop } = useMemo(
    () =>
      getSliderSizes(currentStep, {
        first: firstStepHeight,
        second: secondStepHeight,
      }),
    [currentStep, firstStepHeight, secondStepHeight],
  );

  const {
    mainCategories,
    mainQuestion,
    mainProfileImage,
    mainHeading,
    subHeading,
    mainLinkParamName,
    secondLevelLinkParamName,
    thirdLevelLinkParamName,
  } = blok;

  useEffect(() => {
    if (!wizardData[1]) {
      setNextStep(1, {
        image: mainProfileImage?.filename || '',
        question: mainQuestion || '',
        categories: mainCategories || [],
        linkParams: { [mainLinkParamName]: '' },
      });
    }
  }, [
    mainCategories,
    mainQuestion,
    mainProfileImage,
    mainLinkParamName,
    setNextStep,
    wizardData,
  ]);

  const getLinkParamName = (step: number): string => {
    const paramsMap: Record<number, string> = {
      1: mainLinkParamName,
      2: secondLevelLinkParamName,
      3: thirdLevelLinkParamName,
    };
    return paramsMap[step] || '';
  };

  const handleItemClick = (item: WizardItem) => {
    const {
      subCategories,
      question,
      profileImage,
      linkParamValue,
      isLastStep,
    } = item;
    const nextStep = currentStep + 1;

    if (!isLastStep) {
      setNextStep(nextStep, {
        image: profileImage || '',
        question: question || '',
        categories: subCategories || [],
        linkParams: { [getLinkParamName(currentStep)]: linkParamValue || '' },
      });
    }

    const currentLinkParamName = getLinkParamName(currentStep);

    if (currentLinkParamName && linkParamValue) {
      updateStepData(currentStep, {
        linkParams: {
          [currentLinkParamName]: linkParamValue,
        },
      });
    }

    if (isLastStep) {
      handleRedirect();
    }
  };

  const handleRedirect = () => {
    const { wizardData } = useClothesFinderWizardStore.getState();
    const queryParams = buildQueryParams(wizardData);
    router.push(`?${queryParams}`);
  };

  return (
    <div className={styles.root}>
      <WizardHeader
        image={wizardData[currentStep]?.image || ''}
        heading={mainHeading}
        subHeading={subHeading}
      />
      <div className={styles.questions} style={{ height: sliderHeight }}>
        <div className={styles.questionSlider} style={{ top: sliderTop }}>
          {steps.map((stepIndex, index) => (
            <WizardStep
              key={stepIndex}
              question={wizardData[stepIndex]?.question || ''}
              ref={stepRefs[index] || undefined}
            >
              {renderCategoryItems(
                wizardData[stepIndex]?.categories || [],
                handleItemClick,
                index === steps.length - 1,
              )}
            </WizardStep>
          ))}
        </div>
      </div>
      <WizardSteps activeStep={currentStep} setActive={setCurrentStep} />
    </div>
  );
};
